import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import dayjs from 'dayjs';
import { Button } from '@/components/Button';
import { DateRangeFilter } from '@/components/DatePicker';

// The Warehouse lists' "Filter" drawer — same look and behaviour as the
// Shipments filter (ShipmentFilter), driven by a field list so each page
// declares only what it filters on (the logistic web's filterFields, minus
// the ops-only Client / Vendor / upload / storage pickers).
//
// Values are the API's own query params: a `date` field with key `delivery`
// writes `deliveryFrom` / `deliveryTo`, an `enum` / `text` field writes its
// key. So the applied values go to the list, its tab counts and its CSV as-is.

export type FilterValues = Record<string, string>;

export type FilterField =
  | {
      kind: 'date';
      /** Param prefix: `date` → dateFrom / dateTo. */
      key: string;
      label: string;
      /** Chip presets looking back (created) or ahead (delivery, expiry). */
      presets: 'past' | 'future';
    }
  | {
      kind: 'enum';
      key: string;
      label: string;
      options: { value: string; label: string }[];
    }
  | { kind: 'text'; key: string; label: string; placeholder?: string };

/** A date range counts once; empty values don't count. */
export const countActiveFilters = (
  fields: readonly FilterField[],
  values: FilterValues
): number =>
  fields.filter((f) =>
    f.kind === 'date'
      ? !!(values[`${f.key}From`] || values[`${f.key}To`])
      : !!values[f.key]
  ).length;

const sectionLabel =
  'mb-2 text-xs font-semibold uppercase tracking-wide text-text-soft-400';

const toISODate = (d: Date | null) => (d ? dayjs(d).format('YYYY-MM-DD') : '');
const fromISODate = (s?: string) => (s ? new Date(s) : null);

const day = (offset: number) => dayjs().add(offset, 'day').format('YYYY-MM-DD');

const PRESETS: Record<
  'past' | 'future',
  { label: string; range: () => [string, string] }[]
> = {
  past: [
    { label: 'Today', range: () => [day(0), day(0)] },
    { label: 'Yesterday', range: () => [day(-1), day(-1)] },
    { label: 'Last 7 Days', range: () => [day(-7), day(0)] },
    { label: 'Last 30 Days', range: () => [day(-30), day(0)] },
  ],
  future: [
    { label: 'Today', range: () => [day(0), day(0)] },
    { label: 'Tomorrow', range: () => [day(1), day(1)] },
    { label: 'Next 7 Days', range: () => [day(0), day(7)] },
    { label: 'Next 30 Days', range: () => [day(0), day(30)] },
  ],
};

// Same chip as the Shipments filter.
const presetChip = (active: boolean) =>
  `rounded-lg border px-3.5 py-[7px] text-[12.5px] font-semibold transition-colors ${
    active
      ? 'border-primary bg-primary text-static-white'
      : 'border-stroke-sub-300 text-text-sub-600 hover:border-primary'
  }`;

const WarehouseFilter: React.FC<{
  /** Drawer subtitle, e.g. "Outbound request". */
  title: string;
  fields: readonly FilterField[];
  value: FilterValues;
  onApply: (value: FilterValues) => void;
}> = ({ title, fields, value, onApply }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<FilterValues>(value);

  // Re-seed the draft from the applied value each time the drawer opens.
  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  const activeCount = countActiveFilters(fields, value);
  const set = (patch: FilterValues) => setDraft((d) => ({ ...d, ...patch }));

  const apply = () => {
    // Drop blanks so the query string (and the active count) stay clean.
    onApply(Object.fromEntries(Object.entries(draft).filter(([, v]) => v)));
    setOpen(false);
  };

  const reset = () => {
    setDraft({});
    onApply({});
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="relative inline-flex items-center gap-2 rounded-md border border-stroke-sub-300 bg-bg-white-0 px-3 py-2 text-sm text-text-sub-600 hover:bg-bg-weak-50"
      >
        <i className="ri-filter-3-line" />
        Filter
        {activeCount > 0 && (
          <span className="ml-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-xs font-semibold text-static-white">
            {activeCount}
          </span>
        )}
      </button>

      {open &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-40 bg-bg-strong-950/40"
              aria-hidden="true"
              onClick={() => setOpen(false)}
            />
            <div
              role="dialog"
              aria-label={`Filter ${title}`}
              className="fixed right-0 top-0 z-50 flex h-full w-[420px] max-w-[92vw] flex-col bg-bg-white-0 shadow-card-lg"
            >
              <div className="flex shrink-0 items-center justify-between border-b border-stroke-soft-200 px-5 py-4">
                <div>
                  <h2 className="text-lg font-semibold text-text-strong-950">
                    Filter
                  </h2>
                  <p className="text-xs text-text-sub-600">{title}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="text-text-soft-400 hover:text-text-sub-600"
                  aria-label="Close filter"
                >
                  <i className="ri-close-line text-xl" />
                </button>
              </div>

              <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5">
                {fields.map((f) => {
                  if (f.kind === 'date') {
                    const fromKey = `${f.key}From`;
                    const toKey = `${f.key}To`;
                    return (
                      <div key={f.key}>
                        <h4 className={sectionLabel}>{f.label}</h4>
                        <div className="mb-3 flex flex-wrap gap-2">
                          {PRESETS[f.presets].map((p) => {
                            const [from, to] = p.range();
                            const active =
                              draft[fromKey] === from && draft[toKey] === to;
                            return (
                              <button
                                key={p.label}
                                type="button"
                                onClick={() =>
                                  set(
                                    active
                                      ? { [fromKey]: '', [toKey]: '' }
                                      : { [fromKey]: from, [toKey]: to }
                                  )
                                }
                                className={presetChip(active)}
                              >
                                {p.label}
                              </button>
                            );
                          })}
                        </div>
                        {/* Remount when a chip changes the range so the
                            picker's label follows it. */}
                        <DateRangeFilter
                          key={`${draft[fromKey]}-${draft[toKey]}`}
                          onDateRangeChange={(r) =>
                            set({
                              [fromKey]: toISODate(r.from),
                              [toKey]: toISODate(r.to),
                            })
                          }
                          initialRange={{
                            from: fromISODate(draft[fromKey]),
                            to: fromISODate(draft[toKey]),
                          }}
                        />
                      </div>
                    );
                  }
                  if (f.kind === 'text') {
                    return (
                      <div key={f.key}>
                        <h4 className={sectionLabel}>{f.label}</h4>
                        <input
                          type="text"
                          value={draft[f.key] ?? ''}
                          placeholder={f.placeholder}
                          onChange={(e) => set({ [f.key]: e.target.value })}
                          className="h-10 w-full rounded-lg border border-stroke-soft-200 bg-bg-white-0 px-3 text-sm text-text-strong-950 focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                    );
                  }
                  return (
                    <div key={f.key}>
                      <div className="mb-2 flex items-center justify-between">
                        <h4 className={`${sectionLabel} mb-0`}>{f.label}</h4>
                        {draft[f.key] && (
                          <button
                            type="button"
                            onClick={() => set({ [f.key]: '' })}
                            className="text-xs font-medium text-text-sub-600 hover:text-text-strong-950"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      <div className="space-y-1 rounded-lg border border-stroke-soft-200 p-3">
                        {f.options.map((o) => {
                          const checked = draft[f.key] === o.value;
                          return (
                            <label
                              key={o.value}
                              className="flex cursor-pointer items-center gap-2.5 py-1"
                            >
                              <span
                                className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors ${
                                  checked
                                    ? 'border-primary bg-primary'
                                    : 'border-stroke-sub-300 bg-bg-white-0'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name={`wh-filter-${f.key}`}
                                  className="sr-only"
                                  checked={checked}
                                  // Clearing is the section's "Clear" link.
                                  onChange={() => set({ [f.key]: o.value })}
                                />
                                {checked && (
                                  <span className="h-1.5 w-1.5 rounded-full bg-static-white" />
                                )}
                              </span>
                              <span className="text-sm text-text-strong-950">
                                {o.label}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex shrink-0 items-center justify-between border-t border-stroke-soft-200 px-5 py-4">
                <button
                  type="button"
                  onClick={reset}
                  className="text-sm font-medium text-text-sub-600 hover:text-text-strong-950"
                >
                  Reset
                </button>
                <Button variant="primary" size="medium" onClick={apply}>
                  Apply filter
                </Button>
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
};

export default WarehouseFilter;
